import { WizComponentUtils } from './../../../../utils/wiz-component.utils';
import { WizDividerChildContent } from './../../../../obj/layouts/wiz-divider-child-content';
import { BehaviorSubject } from 'rxjs';

import { DOCUMENT } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, OnInit,
  Renderer2, HostBinding, ViewEncapsulation, Inject
} from '@angular/core';
import { WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';
import { WizRoot } from '../../../views/wiz-root';

/**
 * Interface emise par l'observation du drag
 */
export interface WizGrabberEvent {
  /** Le grabber déplacé */
  grabber?: WizGrabberComponent;
  /** Le mouvement */
  offset?: number;
}

/**
 * Poignée de redimensionnement dans un divider
 */
@Component({
  selector: 'wiz-divider-grabber',
  template: `<span class="partial-span"></span><span class="full-span"></span>`,
  styleUrls: ['./wiz-grabber.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class WizGrabberComponent implements OnInit {
  // Rendus du grabber selon la direction
  @HostBinding('class.h-grabber') get isHDivider(): boolean { return this.direction === 'H'; }
  @HostBinding('class.v-grabber') get isVDivider(): boolean { return this.direction === 'V'; }

  /**
   * Pointeur de fonction de fin de drag pour faciliter le removeEventListener
   */
  private _dragEndFunc: any;

  /**
   * Pointeur de fonction de début de drag pour faciliter le removeEventListener
   */
  private _draggingFunc: any;

  /** La taille totale utile marges incluses, selon la direction */
  private _totalSize: number;

  public get totalSize(): number {
    if (!this._totalSize) {
      // Calcul de la taille totale utils, marges incluses, selon la direction
      const elem = this.elementRef.nativeElement as HTMLElement;
      const grabberRect: DOMRect = WizComponentUtils.getComponentCoordinates(this.elementRef);
      const size: number = this.isHDivider ? grabberRect.width : grabberRect.height;
      let marginBefore: number = parseFloat(this.isHDivider ? elem.style.marginLeft : elem.style.marginTop);
      let marginAfter: number = parseFloat(this.isHDivider ? elem.style.marginRight : elem.style.marginBottom);
      if (!marginBefore) {
        marginBefore = 0;
      }
      if (!marginAfter) {
        marginAfter = 0;
      }
      this._totalSize = size + marginBefore + marginAfter;
    }
    return this._totalSize;
  }

  /**
   * Observable utilisé pour communiquer le drag
   */
  private _grabSubject: BehaviorSubject<WizGrabberEvent>;

  public set grabSubject(pValue: BehaviorSubject<WizGrabberEvent>) {
    this._grabSubject = pValue;
  }

  /**
   * Position de départ au redimensionnement
   */
  private _start: number;

  /** Contenu du divider précédent le grabber */
  private _previousContent: WizDividerChildContent;

  public get previousContent(): WizDividerChildContent {
    return this._previousContent;
  }

  public set previousContent(content: WizDividerChildContent) {
    this._previousContent = content;
  }

  /**
   * Direction du grabber
   */
  private _direction: 'H' | 'V';

  public set direction(pValue: 'H' | 'V') {
    this._direction = pValue;
  }

  public get direction(): 'H' | 'V' {
    return this._direction;
  }

  constructor(public elementRef: ElementRef,
    public renderer: Renderer2,
    protected cdref: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document) { }

  /**
   * Pose les listeners
   */
  ngOnInit() {
    this.elementRef.nativeElement.ondragstart = () => false;

    // Listener souris/touch sur le grabber
    this.elementRef.nativeElement.addEventListener('mousedown', this.onDragStart.bind(this));
    this.elementRef.nativeElement.addEventListener('touchstart', this.onDragStart.bind(this));
    // LMA gestion du double-clic pour ouverture / fermeture rapide
    this.elementRef.nativeElement.addEventListener('dblclick', this.trapDoubleClick.bind(this));
  }

  /**
   * Recherche de l'abscisse de la position d'un évènement souris/touch
   * @param e Evenement souris ou touch
   */
  private getClientX(e: any) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  /**
   * Recherche de l'ordonnée de la position d'un évènement souris/touch
   * @param e Evenement souris ou touch
   */
  private getClientY(e: any) {
    return e.touches ? e.touches[0].clientY : e.clientY;
  }

  /**
   * bubbling du 2xClic pour interception... ou pas.
   * @param e 2xClic
   */
  trapDoubleClick(e: Event) {
    WizEventManager.dispatchEvent(this.elementRef,
      new WizEvent(WIZ_EVENT_TYPE.DRAGGER_DOUBLE_CLICK, null, this));
  }

  /**
   * Handler de début de drag
   * @param e Evenement de début de drag
   */
  onDragStart(e: Event) {
    
    // fire d'un event pour pouvoir bloquer les embed & iframes
    WizEventManager.dispatchEvent(WizRoot.root(), new WizEvent(WIZ_EVENT_TYPE.DRAG_STARTED, null));
    
    // J'alimente la position de départ, les dimensions de l'élément cible et du grabber
    this._start = this.direction === 'H' ? this.getClientX(e) : this.getClientY(e);

    // init des pointeurs de fonction début/fin de drag
    this._dragEndFunc = this.dragEnd.bind(this);
    this._draggingFunc = this.dragging.bind(this);

    // Ajout des listeners souris/touch
    this.document.addEventListener('mouseup', this._dragEndFunc, false);
    this.document.addEventListener('mousemove', this._draggingFunc, false);
    this.document.addEventListener('touchend', this._dragEndFunc, false);
    this.document.addEventListener('touchmove', this._draggingFunc, false);

    // Disable highlighting while dragging
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    if (e.preventDefault) {
      e.preventDefault();
    }

    e.cancelBubble = true;
    e.returnValue = false;

  }

  /**
   * Handler de mouse move ou touch move pour le déplacement du grabber
   *
   * Détermination de la direction du drag et emission du mouvement via l'observable dédié
   * @param e Evenement écouté
   */
  dragging(e: Event) {
    const offset: number = this.direction === 'H' ? this._start - this.getClientX(e) : this._start - this.getClientY(e);
    if (this._grabSubject) {
      this._grabSubject.next({ grabber: this, offset });
      this._start = this.direction === 'H' ? this.getClientX(e) : this.getClientY(e);
      return;
    }
  }

  /**
   * Handler de mouseup ou touchend pour fin de drag
   *
   * Suppression des listeners initialisés au mousedown
   * @param e Evenement de fin de drag
   */
  dragEnd(e: Event) {
    // fire d'un event pour pouvoir bloquer les embed & iframes
    WizEventManager.dispatchEvent(WizRoot.root(), new WizEvent(WIZ_EVENT_TYPE.DRAG_DROPPED, null));
    
    this.document.removeEventListener('mouseup', this._dragEndFunc, false);
    this.document.removeEventListener('mousemove', this._draggingFunc, false);
    this.document.removeEventListener('touchend', this._dragEndFunc, false);
    this.document.removeEventListener('touchmove', this._draggingFunc, false);
  }
}
